/**
 * Workspaces — the tool's sections and the pages inside each one.
 *
 * One source for three things that must agree: the Home page's module entries,
 * the workspace tab row (Navbar), and the per-workspace Field Guide. The current
 * workspace is always derived from the route, never stored, so a deep link and
 * a click land in the same place.
 *
 * Paths are the existing /siop/* routes on purpose: bookmarks from before the
 * workspace split keep resolving. Pages that were taken out of the tab rows
 * (By Category, By AOP Plan, Supply Chain Zones) are redirected in App.jsx and
 * do not appear here.
 */

/** The global Field Guide, and the per-workspace one under it. */
export const FIELD_GUIDE_PATH = '/siop/field-guide';
export const fieldGuidePath = (workspaceKey) => `${FIELD_GUIDE_PATH}/${workspaceKey}`;

/**
 * Each tab: `label`, `path` (where it links to), and optionally `paths` — every
 * route prefix that should highlight it (defaults to `[path]`). Each tab also
 * carries `scope`, the text behind the (i) "Scope of this view" popover.
 */
export const WORKSPACES = [
  {
    key: 'forecasting',
    label: 'Forecasting Analysis',
    description:
      'The demand forecast run by material, KSC and sales manager, against the prior year and the AOP, with the Forecast Analyst.',
    showOnHome: true,
    hasFieldGuide: true,
    tabs: [
      {
        label: 'Forecasting Analysis',
        path: '/forecasting',
        scope: [
          'The latest demand forecast run in Fabric, over its own horizon, against the prior year (once actuals exist) and the products AOP.',
          'Demand in the forecasting scope (NAM; PRD by request date, MRO by billing date), not the sales scope.',
          'Money is the run’s revenue in $K; the Material and SOP Family hierarchies show quantity in each row’s unit of measure.',
          'Period and T/A filters apply to the cards, the charts and the table. The forecast covers NAM only.',
        ],
      },
    ],
  },
  {
    key: 'siop',
    label: 'SIOP Performance',
    description: 'Actuals, backlog and AOP by category, revenue lever and quarter.',
    showOnHome: false,
    hasFieldGuide: true,
    tabs: [
      {
        label: 'Overview',
        path: '/siop/overview',
        // '/siop' only so the tab is highlighted during the index redirect.
        paths: ['/siop/overview', '/siop'],
        scope: [
          'FY2025 actuals, and FY2026 actuals, backlog and AOP, by revenue lever and by category, in USD.',
          'Every filter in the bar applies to the cards, the charts and the tables. Platform applies to Pulse Bridge only.',
          'Sales come from Fabric billing (ZSLSCP), backlog from Fabric (ZBACKCP), AOP from the recon workbook.',
        ],
      },
      {
        label: 'Performance Detail',
        path: '/siop/sub-brand',
        scope: [
          'Category, then sub-brand, then quarter drill-down of the SIOP measures, in USD.',
          'Uses the same filters and sources as Overview.',
        ],
      },
    ],
  },
  {
    key: 'pulse',
    label: 'Pulse Bridge',
    description:
      'Monthly bridge from billed revenue to the revenue target, with the Chemelex Insight Agent.',
    showOnHome: true,
    hasFieldGuide: true,
    tabs: [
      {
        label: 'Monthly',
        path: '/siop/pulse-bridge',
        // Order Rate is reached from the Monthly uploads workspace.
        paths: ['/siop/pulse-bridge', '/siop/order-rate'],
        scope: [
          'Monthly Pulse Bridge for the selected month, region and platform, in USD.',
          'Pulse = Actual Sales + Backlog (Products) + Backlog (Projects) + Funnel + X (Order Rate); TTA = AOP − Pulse.',
          'Backorders & Risks and Unscheduled Orders are drawn beside the bridge; they are not steps in it.',
        ],
      },
      {
        label: 'Products Only',
        path: '/siop/products-pulse',
        scope: [
          'The monthly bridge restricted to product sales: product billing, product backlog and order rate against the products revenue target.',
          'Pulse = Billing (Product) + Backlog (Products) + X × 0.66; TTA = AOP (IHS Products + BIS Products) − Pulse.',
        ],
      },
    ],
  },
  {
    key: 'data',
    label: 'Data Configuration',
    description: 'Yearly reference uploads: standard cost and material lookup.',
    showOnHome: true,
    hasFieldGuide: true,
    tabs: [
      {
        label: 'STD Cost',
        path: '/siop/std-cost',
        scope: [
          'Yearly standard-cost uploads, FY2025 and FY2026, per material.',
          'SOP family and sub-brand are looked up from the Lookup file and are read-only here.',
        ],
      },
      {
        label: 'Lookup',
        path: '/siop/lookup',
        scope: [
          'Yearly Lookup and Standard Prices uploads, used to map materials across the tool.',
        ],
      },
    ],
  },
  {
    key: 'admin',
    label: 'Administration',
    description: 'User management and data-source status.',
    showOnHome: false,
    hasFieldGuide: false,
    tabs: [
      {
        label: 'Settings',
        path: '/settings',
        scope: ['User management and the status of every data source the tool reads.'],
      },
    ],
  },
  {
    key: 'guide',
    label: 'Field Guide',
    description: 'Definitions and calculations for every page.',
    showOnHome: false,
    hasFieldGuide: false,
    tabs: [
      {
        label: 'Field Guide',
        path: FIELD_GUIDE_PATH,
        scope: ['Definitions and calculations for the pages in this tool.'],
      },
    ],
  },
];

const tabPaths = (tab) => tab.paths || [tab.path];

// A path boundary match, not a raw startsWith: '/siop' must not claim every
// '/siop/*' route, and '/siop/pulse-bridge' must not claim '/siop/pulse-bridgex'.
const matchLength = (pathname, prefix) =>
  pathname === prefix || pathname.startsWith(`${prefix}/`) ? prefix.length : -1;

/**
 * The workspace and tab a route belongs to, by longest matching prefix.
 * The Field Guide routes resolve to the workspace whose guide they show.
 * Returns `{ workspace: null, tab: null }` for routes outside every workspace
 * (Home, for one).
 */
export function resolveWorkspace(pathname) {
  const clean = (pathname || '').replace(/\/+$/, '') || '/';

  if (matchLength(clean, FIELD_GUIDE_PATH) >= 0) {
    const key = clean.slice(FIELD_GUIDE_PATH.length + 1).split('/')[0];
    const owner = WORKSPACES.find((w) => w.key === key && w.hasFieldGuide);
    if (owner) return { workspace: owner, tab: null, isFieldGuide: true };
    // The global guide (or an unknown key): its own single-tab workspace.
    const guide = WORKSPACES.find((w) => w.key === 'guide');
    return { workspace: guide, tab: guide.tabs[0], isFieldGuide: false };
  }

  let best = { workspace: null, tab: null, len: -1 };
  for (const workspace of WORKSPACES) {
    for (const tab of workspace.tabs) {
      const len = Math.max(...tabPaths(tab).map((p) => matchLength(clean, p)));
      if (len > best.len) best = { workspace, tab, len };
    }
  }
  return { workspace: best.workspace, tab: best.tab, isFieldGuide: false };
}

/** Where a workspace's Home entry opens: its first internal tab. */
export const workspaceEntry = (workspace) => workspace.tabs[0]?.path;
